<template>
    <div>
        <!-- 평가 요약 영역 -->
        <b-row class="mb-3">
            <b-col cols="12">
                <b-card no-body class="p-3 evaluation-summary" :class="evaluationLevelMeta.cardClass">
                    <b-row no-gutters class="align-items-center">
                        <b-col class="col-auto pr-3 mb-1 mb-md-0 d-flex align-items-center">
                            <span class="level-label mr-2">문항 수준</span>
                            <b-badge :variant="evaluationLevelMeta.variant" class="level-badge px-3 py-2">
                                {{ evaluationLevel }}
                            </b-badge>
                        </b-col>
                    </b-row>
                </b-card>
            </b-col>
        </b-row>
        <!-- /평가 요약 영역 -->
        <b-row no-gutters class="mx-0 border-bottom border-color-gray-300 pb-1 mb-2 gray-550">
            <b-col class="col-9 f-90"> 평가기준</b-col>
            <b-col class="col-2 text-center f-90"> 수준</b-col>
            <b-col class="col-1 text-center f-90"> 초기화</b-col>
        </b-row>
        <b-row v-for="(item, index) in ev_scoring_list" :key="`subs-${sub_code}-ev-${item.ev_info.ev_id}`"
               no-gutters
               :class="['d-flex justify-content-bewteen align-items-center mx-0 mb-3', { 'bg-blue2': is_audited(item) }]"
        >
            <b-col class="col-9" style="margin-top: 12px">
                <b-form-checkbox v-model="item.ev_result.satisfied"
                                 class="f-95"
                                 style="margin-left: 12px"
                                 :disabled="item.ev_result.not_applicable || is_not_applicable === null || is_updating_ev_scoring || disabled"
                                 @change="toggleManualSatisfied"
                >
                    {{ item.ev_info.name }}
                </b-form-checkbox>
            </b-col>
            <b-col cols="2" :class="[{ 'red5 f-90': item.ev_result.not_applicable }, 'text-center']">
                        <b-badge :class="[{ 'gray-550': is_updating_ev_scoring || disabled }]">
                            {{ itemEvCode(item) }}
                        </b-badge>
            </b-col>
            <b-col class="col-1 text-center">
                <b-button class="py-1" :variant="item.ev_result.author_id !== null ? 'icon-blue' : 'icon-dark'"
                          :disabled="item.ev_result.not_applicable || is_not_applicable === null || is_updating_ev_scoring || !is_audited(item) || disabled"
                          @click="resetEvaluation(item,index)"
                >
                    <fa icon="history"></fa>
                </b-button>
            </b-col>
            <b-col class="col-12 d-flex" style="margin-bottom: 12px">
                <template v-if="is_audited(item)">
                    <fa icon="comment-dots" class="ml-3 mr-2 mt-4 blue7"></fa>
                </template>
                <template v-else>
                    <div class="ml-3 mr-2" style="width: 15px"></div>
                </template>
                <div class="audit-container">
                    <b-form-select
                        v-model="item.ev_result.audit_category_code"
                        class="audit-category"
                        :disabled="item.ev_result.not_applicable || is_updating_ev_scoring || is_not_applicable === null || disabled"
                        @change="handleChange({type: 'category', item, index})"
                    >
                        <b-form-select-option v-for="category in audit_category"
                                              :key="category.code"
                                              :value="category.code"
                                              :disabled="category.code == null"
                        >
                            {{ get_audit_category_name(category) }}
                        </b-form-select-option>
                    </b-form-select>
                    <b-form-textarea
                        v-model="item.ev_result.audit_comment"
                        placeholder="평가 항목별 의견"
                        :class="[{ 'blue7 italic': is_audited(item) },'f-95 audit-comment']"
                        :disabled="item.ev_result.not_applicable || is_updating_ev_scoring || is_not_applicable === null || disabled"
                        :rows="6"
                        :max-rows="6"
                        @blur="handleChange({type: 'comment', item, index})"
                    >
                    </b-form-textarea>
                </div>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import {debounce} from 'lodash';
import {mapGetters, mapState} from 'vuex';

export default {
    name: 'UserEvaluationComponentV2',
    props: {
        sub_code: {
            type: String
        },
        ev_scoring_list: {
            type: Array,
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false
        },
        is_updating_ev_scoring: {
            type: Boolean,
            default: false
        },
        is_not_applicable: {
            type: Boolean,
            default: false
        },
        ev_results: {
            type: Object,
            default: () => ({}),
        }
    },
    data() {
        return {};
    },
    computed: {
        ...mapState('evaluations', ['audit_category', 'ev_code_list', 'map_ev_lv_variant']),
        ...mapGetters('evaluations', ['mapEvCodeLv']),
        get_audit_category_name() {
            return (item) => {
                if (this.$i18n.locale === 'ko')
                    return item.category;
                if (this.$i18n.locale === 'en')
                    return item.category_ea;
                if (this.$i18n.locale === 'ca')
                    return item.category_ca;
                return item.category;
            };
        },
        evaluationLevel() {
            const {ev_code} = this.ev_results[this.sub_code];
            if(ev_code == null) return null;
            return this.mapEvCodeLv[ev_code]?.description;
        },
        itemEvCode() {
            return (item) => {
                const ev_code = item.ev_result.pos_ev_code;
                const target = this.ev_code_list.find(e => e.code === ev_code);
                return target?.description || '-';
            };
        },
        evaluationLevelMeta() {
            const {ev_code} = this.ev_results?.[this.sub_code] ?? {};
            if(ev_code == null) return {}; 
            return this.map_ev_lv_variant[ev_code];
        },
        is_audited() {
            return (item) => {
                return item.ev_result.judged_by === 'audit';
            };
        }
    },
    methods: {
        toggleManualSatisfied: debounce(async function () {
            this.$emit('handleChange', this.sub_code);
        }, 2500), // 4500
        async handleChange({type, item, index}) {
            if(type === 'category'){
                if(item.ev_result.audit_category_code !== this.ev_results?.[this.sub_code].ev_results[index].audit_category_code)
                    this.$emit('handleChange', this.sub_code);
            }
            else if(type === 'comment'){
                if(item.ev_result.audit_comment !== this.ev_results?.[this.sub_code].ev_results[index].comment ){
                    if(item.ev_result.audit_comment === '' && this.ev_results?.[this.sub_code].ev_results[index].comment == null) return;
                    this.$emit('handleChange', this.sub_code);
                }
            }

        },
        async resetEvaluation(item, i) {
            const {ev_result} = item;
            if(ev_result.origin_ev_code === 'N' && ev_result.origin_ev_code !== ev_result.ev_code){
                const h = this.$createElement;
                this.$showModal({
                    type: 'alert',
                    state: 'warning',
                    title: this.$t('평가 초기화 실패'),
                    message: h('div', {}, [
                        h('div', {}, this.$t('시스템에서 "해당없음(N)"으로 평가된 항목은 개별 초기화할 수 없습니다.')),
                        h('div', {}, this.$t('초기화를 원하시면 하단의 전체 초기화 버튼을 눌러주시기 바랍니다.')),
                    ]),
                });
            }
            else {
                this.$emit('resetEvaluation', i);
            }
        },
    }
};
</script>

<style lang="scss" scoped>
.evaluation-summary {
    position: relative;
    display: flex;
    align-items: center;

    .level-label {
        font-weight: 600;
        font-size: 0.95rem;
        color: #495057;
    }

    .level-badge {
        font-size: 0.9rem;
        letter-spacing: 1px;
        font-weight: 600;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
        border-radius: 999px;
    }

    &.level-danger {
        background: linear-gradient(90deg, #fff5f5, #ffe3e3);
    }

    &.level-warning {
        background: linear-gradient(90deg, #fff9eb, #ffeac2);
    }

    &.level-normal {
        background: linear-gradient(90deg, #f5f7fa, #e9ecef);
    }

    &.level-good {
        background: linear-gradient(90deg, #f1fbf5, #daf6e4);
    }
}

.audit-desc {
    width: 100%;
    font-size: 0.95rem;
    color: #6c757d;
    margin-bottom: 5px;
}

.audit-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 12px;
    margin-top: 10px;
    margin-right: 12px;

    .audit-category {
    }

    .audit-comment {
        resize: none;
    }
}
</style>
